import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  LogoutRounded,
  RefreshRounded,
  DeleteRounded,
  HomeRounded,
  PersonRounded,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { getTenantProfile, type TenantProfile } from "../api/tenant-auth";
import {
  getTenantDocuments,
  deleteTenantDocument,
  type BenefitDocument,
} from "../api/tenant-documents";
import { BenefitDocumentUpload } from "../components/BenefitDocumentUpload";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useNotifications } from "../components/NotificationProvider";

type TenantPortalPageProps = {
  token: string;
  userName: string;
  onLogout: () => void;
};

export function TenantPortalPage({
  token,
  userName,
  onLogout,
}: TenantPortalPageProps) {
  const [profile, setProfile] = useState<TenantProfile | null>(null);
  const [documents, setDocuments] = useState<BenefitDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [documentToDelete, setDocumentToDelete] =
    useState<BenefitDocument | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { showAlert, showSnackbar } = useNotifications();

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [profileData, docsData] = await Promise.all([
        getTenantProfile(token),
        getTenantDocuments(token),
      ]);
      setProfile(profileData);
      setDocuments(docsData);
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to load data",
        message: "Please try again shortly.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [token]);

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;

    setDeleteLoading(true);
    try {
      await deleteTenantDocument(token, documentToDelete.id);

      setDocuments((prev) =>
        prev.filter((document) => document.id !== documentToDelete.id),
      );
      showSnackbar({
        title: "Document deleted",
        message: "The document has been removed.",
        variant: "info",
      });
      setDocumentToDelete(null);
    } catch (error) {
      showAlert({
        title: "Delete failed",
        message: "Could not delete the document.",
        variant: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "warning";
    }
  };

  if (!profile && isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ borderRadius: 6 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f7fb" }}>
      <Paper
        variant="outlined"
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(14px)",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 3, py: 2 }}
        >
          <Box>
            <Typography
              variant="overline"
              color="primary.main"
              sx={{ fontWeight: 700, letterSpacing: "0.24em" }}
            >
              Tenant Portal
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              My Benefits
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "teal.main",
                fontWeight: 600,
                fontSize: "0.9rem",
              }}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {userName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tenant
              </Typography>
            </Box>
            <Tooltip title="Logout">
              <IconButton onClick={onLogout} color="error" size="small">
                <LogoutRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
        <Stack spacing={3}>
          {isLoading && <LinearProgress sx={{ borderRadius: 6 }} />}

          {/* Profile & Tenancy Info */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            <Paper variant="outlined" sx={{ flex: 1, borderRadius: 6, p: 3 }}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <PersonRounded color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Personal Details
                </Typography>
              </Stack>
              {profile?.user && (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Email:</strong> {profile.user.email}
                  </Typography>
                  {profile.user.phone && (
                    <Typography variant="body2">
                      <strong>Phone:</strong> {profile.user.phone}
                    </Typography>
                  )}
                  {profile.user.dateOfBirth && (
                    <Typography variant="body2">
                      <strong>Date of Birth:</strong> {profile.user.dateOfBirth}
                    </Typography>
                  )}
                  {profile.user.nationalInsuranceNumber && (
                    <Typography variant="body2">
                      <strong>NI Number:</strong>{" "}
                      {profile.user.nationalInsuranceNumber}
                    </Typography>
                  )}
                </Stack>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ flex: 1, borderRadius: 6, p: 3 }}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <HomeRounded color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Current Tenancy
                </Typography>
              </Stack>
              {profile?.tenancy ? (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Property:</strong> {profile.tenancy.property_name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Address:</strong> {profile.tenancy.address_line_1},{" "}
                    {profile.tenancy.city}, {profile.tenancy.postcode}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Room:</strong> {profile.tenancy.room_number}
                    {profile.tenancy.floor_number != null
                      ? ` (Floor ${profile.tenancy.floor_number})`
                      : ""}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Monthly Rent:</strong> £
                    {Number(profile.tenancy.monthly_rent).toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong>{" "}
                    <Chip
                      label={profile.tenancy.status}
                      size="small"
                      color={
                        profile.tenancy.status === "active"
                          ? "success"
                          : "default"
                      }
                      sx={{ borderRadius: 6, fontWeight: 600 }}
                    />
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No active tenancy found.
                </Typography>
              )}
            </Paper>
          </Stack>

          {/* Current Benefits */}
          <Paper variant="outlined" sx={{ borderRadius: 6, p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Upload Proof of Benefits
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload screenshots or PDFs as proof of the benefits you receive.
              Documents will be reviewed by an administrator.
            </Typography>
            <BenefitDocumentUpload token={token} onUploadComplete={loadData} />
          </Paper>

          {/* Uploaded Documents */}
          <Paper variant="outlined" sx={{ borderRadius: 6, p: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Uploaded Documents ({documents.length})
              </Typography>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() => void loadData()}
                  size="small"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 6,
                  }}
                >
                  <RefreshRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {documents.length > 0 ? (
              <Stack spacing={1.5}>
                {documents.map((doc) => (
                  <Card
                    key={doc.id}
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      "&:hover": {
                        boxShadow: 1,
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        py: 1.5,
                        "&:last-child": { pb: 1.5 },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {doc.file_name}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(doc.file_size)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • {new Date(doc.uploaded_at).toLocaleDateString()}
                          </Typography>
                        </Stack>
                        {doc.rejection_reason && (
                          <Typography
                            variant="caption"
                            color="error.main"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            Rejection reason: {doc.rejection_reason}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={doc.status}
                        size="small"
                        color={getStatusColor(doc.status) as any}
                        sx={{
                          borderRadius: 6,
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      />
                      <Tooltip title="Delete document">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDocumentToDelete(doc)}
                          disabled={deleteLoading}
                        >
                          <DeleteRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No documents uploaded yet.
                </Typography>
              </Box>
            )}
          </Paper>
        </Stack>
      </Box>

      <ConfirmDialog
        open={Boolean(documentToDelete)}
        title="Delete document?"
        message={`This will permanently delete ${documentToDelete?.file_name ?? "this document"}.`}
        confirmLabel={deleteLoading ? "Deleting..." : "Delete"}
        onConfirm={() => void handleDeleteDocument()}
        onClose={() => {
          if (!deleteLoading) setDocumentToDelete(null);
        }}
      />
    </Box>
  );
}
